import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useTenantKey } from '../../lib/useTenantKey';
import { InfoTooltip } from '../../components/ui/InfoTooltip';
import { Select } from '../../components/ui/Select';
import { useFieldId } from '../../components/ui/useFieldId';
import { CategoryDot } from '../categories/CategoryDot';

/** The info icon's tooltip, word for word from the spec. */
const SCHEDULING_CATEGORY_TIP =
  'Category is used by the scheduling tool to determine posting times based on your content schedule, which is defined differently based on the types of content you are posting. To add and manage categories, go to Scheduler Settings.';

/**
 * The workspace's scheduling categories, in creation order: the field's options. From the categories
 * endpoint, which only reads (GET /posting-schedule would write General's default times on a first
 * read). The composer reads the same cache entry to decide what a save sends (categoryForSave).
 */
export function useSchedulingCategories() {
  const tenantKey = useTenantKey();
  return useQuery({
    queryKey: ['scheduling-categories', tenantKey],
    queryFn: ({ signal }) => api.listSchedulingCategories(signal),
    enabled: tenantKey !== '',
  });
}

/**
 * The field's value for General, chosen: the schedule every post without a category uses. Never a
 * category's id (those are UUIDs), and never sent: a save turns it into null (categoryForSave).
 */
export const GENERAL = 'general';

/**
 * What a save sends for the post's category, given the field's value - '' (nothing chosen yet),
 * GENERAL, or a category's id - and the categories the field can show:
 *  - `null` for General: chosen, or nothing chosen - a post without a category is on General.
 *  - the id when it is one of those categories: what the field shows is what is saved.
 *  - `undefined` - send nothing, so the server keeps what the post has - for an id the field cannot
 *    show: the list is still loading or did not load, or it has no such category (deleted elsewhere,
 *    or made after the list was read). The field shows its placeholder then, which is not a choice
 *    anyone made: sending the id could be refused for a category that is gone, and sending null
 *    could take a category off a post that has one.
 */
export function categoryForSave(
  value: string,
  categories: ReadonlyArray<{ id: string }> | undefined,
): string | null | undefined {
  if (value === '' || value === GENERAL) return null;
  return categories?.some((c) => c.id === value) ? value : undefined;
}

/**
 * "Scheduling category" in the composer: which posting schedule the post is queued into. General, the
 * schedule every post without a category uses, is always offered, first - so the list is never
 * empty - then the categories that exist; those are made and managed in Scheduler Settings only,
 * never here. Choosing General is the way back from a category.
 *
 * No empty option: Select shows its placeholder only when the value matches no option, and an empty
 * option would hide it. What a new post starts on - General, or nothing chosen - is the composer's
 * call.
 */
export function SchedulingCategoryField({
  value,
  onChange,
  disabled = false,
}: {
  /** GENERAL, a category's id, or '' for nothing chosen yet (the placeholder). */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const id = useFieldId('composer-category');
  const list = useSchedulingCategories();
  const categories = list.data ?? [];

  return (
    <div>
      {/* A label of its own, not FormField's: that takes a string, and the icon sits beside the
          words, outside the label - inside it, its name would become part of the select's. */}
      <div className="mb-1 flex items-center gap-1.5">
        <label htmlFor={id} className="text-sm font-medium text-ink-strong">
          Scheduling category
        </label>
        <InfoTooltip label="About scheduling categories">{SCHEDULING_CATEGORY_TIP}</InfoTooltip>
      </div>
      <Select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        // Blank, not the placeholder, while the list is on its way: whether the field shows the
        // placeholder, General or the post's category depends on that list. A no-break space keeps
        // the field its height meanwhile.
        placeholder={list.isLoading ? '\u00a0' : 'Select a scheduling category'}
        className="w-full max-w-md sm:w-[21.375rem]"
      >
        {/* Neutral, as the General pill in the post lists is: no dot. The variant makes a chosen
            value semibold in the closed field (role=combobox) only: the list is drawn elsewhere,
            outside it. */}
        <option value={GENERAL}>
          <span className="[[role=combobox]_&]:font-semibold">General</span>
        </option>
        {categories.map((c) => (
          <option key={c.id} value={c.id}>
            {/* Inline, not inline-flex, so the closed field still ellipsizes a long name. */}
            <span className="[[role=combobox]_&]:font-semibold">
              <CategoryDot color={c.color} className="mr-2 align-middle" />
              {c.name}
            </span>
          </option>
        ))}
      </Select>
    </div>
  );
}
